<script lang="ts">
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	import { CompoundChart, Loader, Text } from '@Shelly/shelly-design-system';
	import { devices, rooms, user_settings } from 'src/stores';
	import { consumption_rooms } from 'src/stores/features/consumption';
	import { convertToValueAndUnit } from 'src/helpers/units_helpers';
	import { convertCost } from 'src/helpers/utilities';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import { fetchEnergyStatistics } from 'src/components/EnergyMonitoring/helpers/fetch_energy_statistics';
	import { fetchDeviceEnergyStatistics } from 'src/components/EnergyMonitoring/helpers/fetch_device_energy_statistics';
	import {
		buildRoomChartModel,
		getRoomChartPointTotals,
		getPmDeviceIdsKey,
		getPmDevicesInRoom,
		type RoomChartModel,
		type RoomListEntry,
	} from 'src/components/EnergyMonitoring/helpers/energy_room_charts_helpers';
	import type { EnergyMonitoringPeriodId } from 'src/components/EnergyMonitoring/helpers/period_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { buildDemoRoomChartModel } from 'src/components/EnergyMonitoring/helpers/energy_demo_providers';
	import { CONSUMER_MIX_COLORS } from 'src/components/EnergyMonitoring/helpers/energy_demo_data';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmLayerChartFrame from '../EmLayerChartFrame.svelte';
	import EmChartLegend from '../EmChartLegend.svelte';
	import EmWidgetState from '../EmWidgetState.svelte';
	import { getEnergyChartEmptyMessage } from '../helpers/energy_flow_helpers';

	export let roomId: number;
	export let controls: ChartControlsDataInterface;
	export let periodId: EnergyMonitoringPeriodId;
	export let embedded = false;
	export let staticPreview = false;
	export let previewRoomName = 'Living room';
	$: samplePreview = staticPreview || roomId < 0;

	let loadToken = 0;
	let loading = true;
	let loadError = false;
	let roomChart: RoomChartModel | null = null;
	let hiddenSeriesIds: string[] = [];
	let hoveredPoint: { dt: number; valueWh: number } | null = null;
	let hoverPosition = 50;
	let hiddenRoomId = roomId;
	const previewBarHeights = [28, 34, 31, 42, 55, 48, 62, 76, 68, 57, 72, 88, 79, 66, 74, 91];

	$: room =
		(Object.values($rooms) as RoomListEntry[]).find((entry) => Number(entry.id) === roomId) ??
		(samplePreview ? ({ id: roomId, name: previewRoomName } as RoomListEntry) : null);

	$: pmDeviceIdsKey = room ? getPmDeviceIdsKey(room.id, $devices) : '';
	$: if (roomId !== hiddenRoomId) {
		hiddenRoomId = roomId;
		hiddenSeriesIds = [];
	}
	$: visibleChartData = roomChart?.chartData
		? {
				...roomChart.chartData,
				series: roomChart.chartData.series.map((series) => ({
					...series,
					visible: !hiddenSeriesIds.includes(series.name),
				})),
			}
		: null;
	$: isDemoChart = roomChart?.chartData?.id?.startsWith('demo-room-') === true;
	$: pointTotals = getRoomChartPointTotals(roomChart?.chartData, hiddenSeriesIds);
	$: roomLiveData = $consumption_rooms[String(roomId)] ?? $consumption_rooms[roomId];
	$: roomLivePower = samplePreview
		? 742
		: Math.max(
				Number(roomLiveData?._power) ||
					Number(roomLiveData?.in_power ?? 0) + Number(roomLiveData?.ex_power ?? 0),
				0,
			);
	$: summaryMeasurement = convertToValueAndUnit(roomLivePower, 'W');
	$: summaryValue = `${summaryMeasurement.value} ${summaryMeasurement.unit}`;
	$: summaryLabel = $format('energy_monitoring_room_current', { default: 'Current consumption' });
	$: hoverMeasurement = hoveredPoint
		? convertToValueAndUnit(hoveredPoint.valueWh, 'Wh')
		: { value: 0, unit: 'Wh' };
	$: hoverValue = `${hoverMeasurement.value} ${hoverMeasurement.unit}`;
	$: hoverLabel = hoveredPoint
		? $format('energy_monitoring_room_at_time', {
				default: 'Consumption at {time}',
				values: {
					time: new Intl.DateTimeFormat(undefined, {
						month: 'short',
						day: 'numeric',
						hour: '2-digit',
						minute: '2-digit',
					}).format(new Date(hoveredPoint.dt)),
				},
			})
		: '';
	$: reportedCostLabel =
		roomChart?.reportedCost != null
			? convertCost(
					roomChart.reportedCost,
					samplePreview ? 'EUR' : ($user_settings?.currency ?? ''),
				)
			: null;

	const toggleSeries = (seriesId: string) => {
		hoveredPoint = null;
		hiddenSeriesIds = hiddenSeriesIds.includes(seriesId)
			? hiddenSeriesIds.filter((id) => id !== seriesId)
			: [...hiddenSeriesIds, seriesId];
	};

	const updateHoveredPoint = (event: PointerEvent) => {
		if (!pointTotals.length) return;
		const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
		if (bounds.width <= 0) return;
		const ratio = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 0.999999);
		hoverPosition = ratio * 100;
		hoveredPoint = pointTotals[Math.floor(ratio * pointTotals.length)] ?? null;
	};

	const loadRoomChart = async (
		currentRoomId: number,
		currentControls: ChartControlsDataInterface,
		currentPeriodId: EnergyMonitoringPeriodId,
		currentRoom: RoomListEntry | null,
		deviceIdsKey: string,
		useDemoData: boolean,
	) => {
		if (!currentRoom) {
			roomChart = null;
			loading = false;
			return;
		}

		if (useDemoData) {
			roomChart = buildDemoRoomChartModel(currentRoom);
			loading = false;
			return;
		}

		if (!deviceIdsKey) {
			roomChart = null;
			loading = false;
			return;
		}

		const token = ++loadToken;
		loading = true;
		loadError = false;

		try {
			const allDevices = get(devices);
			const roomStats = await fetchEnergyStatistics(
				currentControls,
				currentPeriodId,
				currentRoomId,
			);
			const pmDevices = getPmDevicesInRoom(currentRoom.id, allDevices);
			const deviceEntries = await Promise.all(
				pmDevices.map(async (device) => ({
					device,
					result: await fetchDeviceEnergyStatistics(device, currentControls, currentPeriodId),
				})),
			);

			if (token !== loadToken) return;
			roomChart = buildRoomChartModel(currentRoom, roomStats, deviceEntries, allDevices);
		} catch {
			if (token === loadToken) {
				roomChart = null;
				loadError = true;
			}
		} finally {
			if (token === loadToken) loading = false;
		}
	};

	$: void loadRoomChart(
		roomId,
		controls,
		periodId,
		room,
		pmDeviceIdsKey,
		samplePreview || $energyMonitoringDataMode === 'demo',
	);

	const refresh = () =>
		loadRoomChart(
			roomId,
			controls,
			periodId,
			room,
			pmDeviceIdsKey,
			samplePreview || $energyMonitoringDataMode === 'demo',
		);
	$: emptyMessage =
		!pmDeviceIdsKey && $energyMonitoringDataMode !== 'demo'
			? $format('energy_monitoring_room_no_devices', {
					default: 'No metered devices in this room for the selected period.',
				})
			: getEnergyChartEmptyMessage($devices, (key, fallback) =>
					$format(key, { default: fallback }),
				);
</script>

{#if embedded}
	{#if loading}
		<div class="em-room-loading">
			<Loader size="m" />
		</div>
	{:else if roomChart?.chartData && visibleChartData}
		<div class="em-room-chart-block">
			<div class="em-room-chart-summary" aria-live="polite">
				<Text styleType="body-small" class="text-600">{room?.name}</Text>
				<Text styleType="h2" class="text-600">{summaryValue}</Text>
				<Text styleType="miniature" class="text-color-muted">{summaryLabel}</Text>
				<Text styleType="body-small" class="em-room-chart-total">
					{$format('energy_monitoring_room_overall', { default: 'Overall consumption:' })}
					<strong>{roomChart.totalLabel}</strong>
					{#if reportedCostLabel}<span>· {reportedCostLabel}</span>{/if}
				</Text>
			</div>
			<div
				class="em-room-chart-interactive"
				role="img"
				aria-label={$format('energy_monitoring_room_history_chart', {
					default:
						'Room consumption history. Move across the chart to inspect earlier values.',
				})}
				on:pointermove={updateHoveredPoint}
				on:pointerleave={() => (hoveredPoint = null)}
			>
				{#if hoveredPoint}
					<div class="em-room-chart-tooltip" style={`--em-room-tooltip-x:${hoverPosition}%`}>
						<Text styleType="body-small" class="text-600">{hoverValue}</Text>
						<Text styleType="miniature" class="text-color-muted">{hoverLabel}</Text>
					</div>
				{/if}
				<EmLayerChartFrame height={145} reserveLegendSlot={false}>
					{#if samplePreview || isDemoChart}
						<div
							class="em-room-preview-chart"
							role="img"
							aria-label="Sample room consumption chart"
						>
							{#each previewBarHeights as height, index}
								<span
									style={`--em-room-preview-height:${height}%;--em-room-preview-color:${CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length]}`}
								></span>
							{/each}
						</div>
					{:else}
						<CompoundChart
							chartData={visibleChartData}
							defaultMark="line"
							legend={false}
							colorScheme={[...CONSUMER_MIX_COLORS]}
						/>
					{/if}
				</EmLayerChartFrame>
			</div>
			{#if roomChart.devices.length}
				<EmChartLegend
					class="em-room-chart-legend"
					items={roomChart.devices
						.filter((device) => !device.excludedFromRoomTotal)
						.map((device) => ({
							id: device.seriesKey ?? device.id,
							label: device.title,
							color: device.color,
							valueLabel: device.valueLabel,
						}))}
					interactive
					tokenized
					wrapTokens
					hiddenItemIds={hiddenSeriesIds}
					on:toggle={(event) => toggleSeries(event.detail.id)}
				/>
				<EmChartLegend
					class="em-room-chart-legend"
					items={roomChart.devices
						.filter((device) => device.excludedFromRoomTotal)
						.map((device) => ({
							id: device.id,
							label: `${device.title} · ${$format('energy_monitoring_room_excluded', {
								default: 'Excluded from room total',
							})}`,
							color: 'var(--color-muted)',
							valueLabel: device.valueLabel,
						}))}
					tokenized
					wrapTokens
				/>
			{/if}
		</div>
	{:else if loadError}
		<EmWidgetState
			kind="error"
			message={$format('energy_monitoring_load_error', {
				default: 'Energy data could not be loaded. Please try again.',
			})}
			minHeight="160px"
			onRefresh={refresh}
		/>
	{:else}
		<EmWidgetState
			kind={!pmDeviceIdsKey ? 'setup' : 'empty'}
			message={emptyMessage}
			minHeight="160px"
		/>
	{/if}
{:else}
	<EmWidgetShell
		widgetClass="energy-widget-room"
		title={room?.name ?? $format('label_widget_rooms', { default: 'Room' })}
		infoText={$format('energy_monitoring_rooms_info', {
			default:
				'Consumption history for this room in the selected period, broken down by metered devices.',
		})}
	>
		{#if loading}
			<div class="em-room-loading">
				<Loader size="m" />
			</div>
		{:else if roomChart?.chartData && visibleChartData}
			<div class="em-room-chart-block">
				<div class="em-room-chart-summary" aria-live="polite">
					<Text styleType="body-small" class="text-600">{room?.name}</Text>
					<Text styleType="h2" class="text-600">{summaryValue}</Text>
					<Text styleType="miniature" class="text-color-muted">{summaryLabel}</Text>
					<Text styleType="body-small" class="em-room-chart-total">
						{$format('energy_monitoring_room_overall', { default: 'Overall consumption:' })}
						<strong>{roomChart.totalLabel}</strong>
						{#if reportedCostLabel}<span>· {reportedCostLabel}</span>{/if}
					</Text>
				</div>
				<div
					class="em-room-chart-interactive"
					role="img"
					aria-label={$format('energy_monitoring_room_history_chart', {
						default:
							'Room consumption history. Move across the chart to inspect earlier values.',
					})}
					on:pointermove={updateHoveredPoint}
					on:pointerleave={() => (hoveredPoint = null)}
				>
					{#if hoveredPoint}
						<div
							class="em-room-chart-tooltip"
							style={`--em-room-tooltip-x:${hoverPosition}%`}
						>
							<Text styleType="body-small" class="text-600">{hoverValue}</Text>
							<Text styleType="miniature" class="text-color-muted">{hoverLabel}</Text>
						</div>
					{/if}
					<EmLayerChartFrame height={145} reserveLegendSlot={false}>
						{#if samplePreview || isDemoChart}
							<div
								class="em-room-preview-chart"
								role="img"
								aria-label="Sample room consumption chart"
							>
								{#each previewBarHeights as height, index}
									<span
										style={`--em-room-preview-height:${height}%;--em-room-preview-color:${CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length]}`}
									></span>
								{/each}
							</div>
						{:else}
							<CompoundChart
								chartData={visibleChartData}
								defaultMark="line"
								legend={false}
								colorScheme={[...CONSUMER_MIX_COLORS]}
							/>
						{/if}
					</EmLayerChartFrame>
				</div>
				{#if roomChart.devices.length}
					<EmChartLegend
						class="em-room-chart-legend"
						items={roomChart.devices.map((device) => ({
							id: device.seriesKey ?? device.id,
							label: device.title,
							color: device.color,
							valueLabel: device.valueLabel,
						}))}
						interactive
						tokenized
						wrapTokens
						hiddenItemIds={hiddenSeriesIds}
						on:toggle={(event) => toggleSeries(event.detail.id)}
					/>
				{/if}
			</div>
		{:else if loadError}
			<EmWidgetState
				kind="error"
				message={$format('energy_monitoring_load_error', {
					default: 'Energy data could not be loaded. Please try again.',
				})}
				minHeight="160px"
				onRefresh={refresh}
			/>
		{:else}
			<EmWidgetState
				kind={!pmDeviceIdsKey ? 'setup' : 'empty'}
				message={emptyMessage}
				minHeight="160px"
			/>
		{/if}
	</EmWidgetShell>
{/if}

<style lang="scss">
	.em-room-loading {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 120px;
	}

	.em-room-chart-block {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		width: 100%;
		min-width: 0;
	}

	.em-room-chart-summary {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 2px;
		min-width: 0;
		padding: 0 var(--spacing-xxs);
	}

	:global(.em-room-chart-total) {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xxs);
		margin-top: var(--spacing-xxs);
		color: var(--color-muted);
	}

	:global(.em-room-chart-total strong) {
		color: var(--color-on-surface);
		font-weight: 600;
	}

	.em-room-chart-interactive {
		position: relative;
		width: 100%;
		min-width: 0;
	}

	.em-room-chart-tooltip {
		position: absolute;
		left: clamp(72px, var(--em-room-tooltip-x), calc(100% - 72px));
		top: var(--spacing-xs);
		z-index: 3;
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 144px;
		padding: var(--spacing-xs) var(--spacing-s);
		transform: translateX(-50%);
		border: 1px solid var(--color-4);
		border-radius: var(--radius-medium);
		background: var(--surface-glass-fallback-bg);
		box-shadow: var(--shadow-m);
		pointer-events: none;
	}

	.em-room-preview-chart {
		display: flex;
		align-items: flex-end;
		gap: var(--spacing-xxs);
		width: 100%;
		height: 100%;
		min-height: 140px;
		padding: var(--spacing-s) var(--spacing-xs) 0;
		border-bottom: 1px solid var(--color-4);
	}

	.em-room-preview-chart span {
		flex: 1 1 0;
		height: var(--em-room-preview-height);
		min-width: 2px;
		border-radius: var(--radius-small) var(--radius-small) 0 0;
		background: var(--em-room-preview-color);
		opacity: 0.86;
	}

	.em-room-chart-block :global(.em-room-chart-legend.chart-token-list) {
		justify-content: center;
		margin-top: var(--spacing-m);
		padding: 0 var(--spacing-xxs);
	}

	.em-room-chart-block :global(.em-room-chart-legend .chart-token) {
		max-width: min(100%, 220px);
	}
</style>
